import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAppSelector } from "@/store/hooks";

interface Course {
  _id: string;
  slug: string;
  title: string;
  image?: string;
  description?: string;
  price: number;
  oldPrice?: number;
  validity?: string;
  languages?: string[];
  buyLink?: string;
}

const Books: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);
  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  const isApiSuccess = (res: any) =>
    res?.data?.status === true || res?.data?.success === true;

  // Fetch all courses
  useEffect(() => {
    if (!token) return;

    axios
      .get(`${baseUrl}/books`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (isApiSuccess(res) && Array.isArray(res.data.data)) {
          setBooks(res.data.data);
        } else {
          setBooks([]);
          console.warn("Unexpected response:", res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch courses:", err.response?.data || err.message);
      });
  }, [baseUrl, token]);

  // Delete course
// Delete course by ID
const handleDelete = async (id: string) => {
  if (!window.confirm("Are you sure you want to delete this course?")) return;

  try {
    const res = await fetch(`${baseUrl}/books/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();

    if (res.ok && data?.status) {
      setBooks(prev => prev.filter(b => b._id !== id));
      alert("✅ Book deleted successfully");
    } else {
      alert(data?.message || "Failed to delete book");
    }
  } catch (err) {
    console.error("❌ Error deleting book:", err);
    alert("Failed to delete book.");
  }
};

// Edit book by ID
const handleEdit = (id: string) => {
  navigate(`/books/edit/${id}`);
};



  // Pagination + Search
  const filteredBooks = books.filter(
    (book) =>
      book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredBooks.length / entriesPerPage) || 1;
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentBooks = filteredBooks.slice(startIndex, endIndex);

  return (
    <div className="main-container container-fluid">
      {/* PAGE HEADER */}
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <a href="/dashboard">Home</a>
          </li>
          <li className="breadcrumb-item active">Books</li>
        </ol>
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={() => navigate("/books/add")}
        >
          <Plus size={16} /> Add Book
        </button>
      </div>

      {/* COURSES TABLE */}
      <div className="card">
        <div className="card-body">
          {/* Controls */}
          <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
            <div>
              Show{" "}
              <select
                className="form-select form-select-sm d-inline w-auto mx-1"
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>{" "}
              entries
            </div>
            <div>
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search Course..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>S.No</th>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Price</th>
                  {/* <th>Validity</th> */}
                  <th>Languages</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentBooks.length ? (
                  currentBooks.map((book, idx) => (
                    <tr key={book._id}>
                      <td>{startIndex + idx + 1}</td>
                      <td>
                        {book.image ? (
                          <img
                            src={book.image}
                            alt={book.title}
                            style={{ height: "60px", borderRadius: "6px" }}
                          />
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                      <td>{book.title}</td>
                      <td>
                        ₹{book.price}{" "}
                        {book.oldPrice && (
                          <small className="text-muted ms-1 text-decoration-line-through">
                            ₹{book.oldPrice}
                          </small>
                        )}
                      </td>
                      {/* <td>{book.validity || "-"}</td> */}
                      <td>{book.languages?.join(", ") || "-"}</td>
                      <td>
                        <button
                          onClick={() => handleEdit(book._id)}
                          className="btn btn-sm btn-outline-primary me-2"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(book._id)}
                          className="btn btn-sm btn-outline-danger"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      No Books found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div>
              Showing {filteredBooks.length ? startIndex + 1 : 0} to{" "}
              {Math.min(endIndex, filteredBooks.length)} of {filteredBooks.length} entries
            </div>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                >
                  Prev
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => (
                <li
                  key={i}
                  className={`page-item ${i + 1 === currentPage ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                >
                  Next
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Books;
